import { notification } from 'antd';
import { useMemo, useState } from 'react';

import { BankNames, DictTypes } from '@/constants';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { setDictionaryOptions } from '@/services';
import { handleError } from '@/services/handleError';
import { modalTsDtoMapper } from '@/services/mappers/modalTsDtoMapper';
import { useAppDispatch, useAppSelector } from '@/store';
import { setShouldUpdateBankInfo } from '@/store/financingStep.slice';
import { setTsModalValues } from '@/store/newOrder.slice';
import type { TBaseModalTSDtoBanks } from '@/types/financing';

import type { VehicleFormInterface } from '../types/vehicle-form';

interface IProps {
  onClose: () => void;
  storageKey: string;
}

const useSaveVehicleInfo = ({ onClose, storageKey }: IProps) => {
  const dispatch = useAppDispatch();

  const { dicts } = useAppSelector((state) => state.dict);
  const { userInfo } = useAppSelector((state) => state.userInfo);
  const { selectedBank } = useAppSelector((state) => state.financingStep);
  const { tsModalValues } = useAppSelector((state) => state.newOrder);

  const [saveLoading, setSaveLoading] = useState(false);

  const registerCompanies = useMemo(
    () =>
      (
        setDictionaryOptions(DictTypes.VehicleRegistrationCompanies, dicts) ||
        []
      ).map((item) => ({ value: String(item.value), label: item.label })),
    [dicts]
  );

  const onSubmit = async (values: VehicleFormInterface) => {
    setSaveLoading(true);
    const currentValues = {
      ...tsModalValues,
      ...values,
      registerCompany:
        registerCompanies.find((item) => item.label === values.registerCompany)
          ?.value || tsModalValues.registerCompany
    };
    dispatch(setTsModalValues(currentValues));

    try {
      const mappedTSInfo = modalTsDtoMapper(currentValues);
      await autoCreditApi.updateVehicleInfo(
        selectedBank?.bank.name === BankNames.ffb
          ? {
              request_id: mappedTSInfo.request_id,
              bank: BankNames.ffb,
              vehicleIdNumber: mappedTSInfo.vehicleIdNumber,
              response_id: selectedBank?.id || '',
              ext_uuid: selectedBank?.extId || ''
            }
          : selectedBank?.bank.name === BankNames.euBankV2
            ? {
                request_id: mappedTSInfo.request_id,
                bank: BankNames.euBankV2,
                vehicleIdNumber: mappedTSInfo.vehicleIdNumber,
                plateNumber: mappedTSInfo.plateNumber,
                ptsCertificateNumber: mappedTSInfo.ptsCertificateNumber,
                ptsRegistrationDate: mappedTSInfo.ptsRegistrationDate
              }
            : {
                ...mappedTSInfo,
                carRegistrationIssuer:
                  Number(currentValues.registerCompany) || 0,
                bank: selectedBank?.bank.name as TBaseModalTSDtoBanks,
                response_id: selectedBank?.id || '',
                ext_uuid: selectedBank?.extId || ''
              }
      );

      dispatch(setShouldUpdateBankInfo(selectedBank?.bank.name));
      onClose();
      notification.info({
        title: 'Данные ТС были добавлены.',
        duration: 5
      });
      if (userInfo) {
        sessionStorage.removeItem(storageKey);
      }
    } catch (error) {
      handleError(error, {
        customTitle: 'Ошибка загрузки данных ТС.'
      });
    }
    setSaveLoading(false);
  };

  return { saveLoading, onSubmit };
};

export default useSaveVehicleInfo;
